
import { T, always, cond, equals, filter, head, includes, map, tail, pathOr, find, path } from 'ramda';
import { canGuard } from 'src/features/RBAC/Can';
import { isEmptyOrNil, isNotEmpty } from 'src/utilities/hasValue';
import { Node, NodeProp } from 'src/utilities/node';
import { Tree, getAdjacentNodes } from 'src/utilities/tree';
 
import { executeConditions, getResourceMatcher } from "src/features/core/Intents/ApiContext/conditions";

interface ChildrenStage {
    deactivated_upon?: {
        apply_logical: string;
        conditions: {
            satisfies: string;
            arguments: any[];
        }[];
    };
    stages: Stage[]
}

interface Stage {
    name: string;
    display_name: string;
    order_id?: number;
    options?: any;
    children?: ChildrenStage[];
    child_of?: {
        all: boolean;
        stages: string[];
        exception_stages: string[];
    }
}

interface StageConfig {
    id: string;
    namespace_id: string;
    loan_type: string;
    sub_loan_types: string[];
    stages: Stage[];
    roles: string[];
}

type StageField = 'namespace_id' |
    'loan_type' |
    'sub_loan_type' |
    'role'


class StageTreeBuilder {
    stageConfig: StageConfig;
    tree?: Tree;
    permissions?: any;
    loanApp: any;

    constructor(stageConfig: StageConfig, permissions?: any,loanApp?:any) {
        this.stageConfig = stageConfig;
        this.permissions = permissions;
        this.loanApp = loanApp;
        this.tree = this.makeTree()
    }

     makeChildren(stage: Stage) {
        // TODO: have to add conditional child sets
        const children =  this.getStageBasedOnConditions(this.getChildrenByPermission(stage))
        if (isNotEmpty(stage)) {
            const options = path(['options'], stage)

            const parent = new Node({
                name: stage.display_name,
                value: stage.name,
                options
            })
            parent.children = filter(
                isNotEmpty,
                map((stage: Stage) => {
                    return  this.makeChildren(stage)
                }, pathOr([], ['stages'], children))
            )
            return parent
        }
        return
    }

     getStageBasedOnConditions(stage: Stage) {
        const resourceMatcher = getResourceMatcher(this.loanApp);
        const stages = pathOr([], ['stages'], stage)

        const mappedStage = filter((stage: ChildrenStage) => {
            const showWhen = pathOr([], ["show_when", "conditions"], stage)
            const applyLogical = pathOr("any", ["apply_logical"], showWhen)
            const isSatisfies = executeConditions([], showWhen, applyLogical, {}, resourceMatcher);


            return isEmptyOrNil(showWhen) ? true : isSatisfies;

        }, stages)
        return {
            stages: isNotEmpty(mappedStage) ? mappedStage : stages
        }
    }
    makeTree() {
        const root = head(this.stageConfig.stages)
        if (isNotEmpty(root) && isNotEmpty(tail(this.stageConfig.stages))) {
            const tree = new Tree({
                name: root?.name,
                value: root?.display_name
            });
            tree._root.children = filter(
                isNotEmpty, map((stage: Stage) => {
                    return this.makeChildren(stage)
                }, this.stageConfig.stages))
            return tree
        }
        return
    }

    hasMatch(value: string, field: StageField) {
        return cond([
            [equals('namespace_id'), always(equals(value, this.stageConfig.namespace_id))],
            [equals('loan_type'), always(equals(value, this.stageConfig.loan_type))],
            [equals('sub_loan_type'), always(includes(value, this.stageConfig.sub_loan_types))],
            [equals('role'), always(includes(value, this.stageConfig.roles))],
            [T, always(false)]
        ])(field)
    }

    getChildrenByPermission(stage: Stage) {
        const children = pathOr([], ['children'], stage)
        const result = find((item: any) => isNotEmpty(pathOr('', ['permission'], item)) ? canGuard(this.permissions, pathOr('', ['permission'], item)) : item, children)
        return result
    }
    nextStages(status: string) {
        const nodes = this.tree ? getAdjacentNodes(this.tree._root, status) : [];
        return map(({ data }: Node<NodeProp>) => {
            return {
                value: pathOr('', ['value'], data),
                display_name: pathOr('', ['name'], data),
                options: path(['options'], data)
            };
        }, nodes)
    }

    nextStageOptions(
        status: string,
        onClickOption: (value: string, options: any) => void,
    ) {
        const nodes = this.tree ? getAdjacentNodes(this.tree._root, status) : [];

        return map(({ data }: Node<NodeProp>) => {
            const options = pathOr('', ['options'], data)
            return {
                name: pathOr('', ['name'], data),
                onClick: () => {
                    return onClickOption(pathOr('', ['value'], data), options);
                },
                className: '',
                options,
            };
        }, nodes)

    }

    reInitialize() {
        this.tree = this.makeTree()
    }

    getTree() {
        return this.tree
    }

    getRootNode() {
        return this.tree?._root
    }


}

export {
    StageTreeBuilder
}